import { workdayDeadline, workdaysBetween } from "../utils/workdays";
import type { LeadContactEvidenceView } from "./contactEvidence";
import type { LeadCustomerSignal } from "./contactPerson";
import { leadOriginLabel } from "./leadRowSignals";

// ---------------------------------------------------------------------------
// Wer verteilt Leads — und wie lange wartet ein unverteilter schon (LEAD-2/3/8)
// ---------------------------------------------------------------------------
// Daniel 2026-07-30: «sandro (CEO) verteilt alle leads, jaqueline soll sie
// auch sehen und soll ihn auch vertreten (assign) koennen» und «unassigned
// sollen bei den PL's nicht angezeigt werden!».
//
// Daniel 2026-08-05: «die leads verteilen, das macht jetzt marvin».
//
// Beides ist EINE Frage — «wer verteilt?» —, und sie wird hier beantwortet,
// nicht an fuenf Orten halb. Konsumenten:
//   * `loadLeadDistributorUserIds` (die Empfaenger der zwei Lead-Meldungen)
//   * `UnassignedLeadsService.list` (Endpoint-Gate der Kachel)
//   * `ProjectLeadService.assignOwner` (der Schreibweg)
//   * `UnassignedLeadsTile` + die Meldungs-Zeile (zeigt die Knoepfe)
// Der Waechter `tests/lead-distribution-guard.test.ts` haelt sie zusammen.
//
// ---------------------------------------------------------------------------
// ZWEI UHREN, und der Unterschied ist Absicht
// ---------------------------------------------------------------------------
// `waitingWorkdays` zaehlt ARBEITSTAGE (Prinzip 16). Das OS stellt am
// Wochenende nichts zu; dann darf es Samstag und Sonntag auch nicht
// mitzaehlen, sonst mahnt es fuer Tage, an denen es selbst geschwiegen hat.
//
// Die 24-Stunden-Frist gekaufter Leads zaehlt dagegen KALENDERZEIT. Sie ist
// keine OS-interne Mahnung, sondern eine Zusage nach aussen: Aroundhome
// schreibt in jede Lead-Mail «Bitte kontaktieren Sie den Interessenten
// innerhalb der naechsten 24 Stunden» und verkauft denselben Lead an vier bis
// fuenf Firmen. Diese Uhr laeuft samstags weiter, egal was das OS tut — wer
// sie in Arbeitstage umrechnet, macht aus einer verpassten Frist ein «alles
// im gruenen Bereich».
//
// LEAD-13 (Daniel 2026-08-19) setzt die zweite Uhr fuer alle UEBRIGEN Leads:
// «Verteil-Frist fuer Leads OHNE eigene Frist → 1 Arbeitstag». Bis dahin hatte
// ein Lead aus dem Website-Formular oder von Hand gar keine Frist — bei ihm
// konnte nie etwas reissen, und Sandro erfuhr nie davon. Diese Uhr ist eine
// HAUSINTERNE Mahnung und zaehlt darum Arbeitstage, nicht Kalenderzeit: der
// Freitagnachmittags-Lead ist am Montag faellig, nicht am Samstag.
//
// Herkunft kommt aus dem KENNZEICHEN (`deriveLeadOrigin` liest
// `lead_intakes.payload.metadata.form_type`), nie aus einem Anzeigetext —
// die INOBAT_EGS257-Lehre.

/**
 * Das kleinste Stueck Nutzer, das die Frage «darf der verteilen?» beantwortet.
 * Bewusst KEIN `role` — die Rolle kann den Fall nicht mehr ausdruecken.
 */
export interface LeadDistributorShape {
  readonly mayDistributeLeads: boolean;
  /**
   * LEAD-10: das routinemaessige SEHEN, getrennt vom DUERFEN darueber. Bis
   * dahin trug ein einziges Haekchen beide Wirkungen — wer es verlor, verlor
   * mit den Karten auch den Knopf.
   */
  readonly watchesUnassignedLeads: boolean;
}

/**
 * LEAD-8 (Daniel 2026-08-05): die Verteil-Berechtigung haengt am MENSCHEN.
 *
 * Bis hierher stand hier eine Rollen-Liste `[admin, buchhaltung, superadmin]`.
 * Sie konnte den neuen Fall nicht mehr abbilden: Marvin soll verteilen, Corsin
 * nicht — und beide sind `pl`. Haetten wir `pl` in die Liste genommen, saehe
 * Corsin ab sofort wieder alle unverteilten Leads, also genau das, was LEAD-3
 * («unassigned sollen bei den PL's nicht angezeigt werden») abgeschafft hat.
 *
 * Es gibt darum genau EINEN Traeger: `users.may_distribute_leads`
 * (Migration 0140). Keine Rolle daneben, die auseinanderlaufen koennte —
 * die Migration hat den Bestand (admin/buchhaltung/superadmin) 1:1 uebernommen
 * und Marvin ergaenzt.
 */
export function canDistributeLeads(user: LeadDistributorShape): boolean {
  return user.mayDistributeLeads;
}

// ---------------------------------------------------------------------------
// LEAD-10 — «Sandro soll nur Lead-Meldungen erhalten, wenn Marvin und
// Jacqueline die Deadline verpassen» (Daniel 2026-08-18)
// ---------------------------------------------------------------------------
// LEAD-8 gab `may_distribute_leads` zwei Wirkungen auf einmal: die Karten
// SEHEN und zuteilen DUERFEN. Als Sandro die Lead-Arbeit abgab, wurde das
// Merkmal auf `false` gestellt — und nahm ihm beides. Fuer eine Eskalation ist
// das genau verkehrt herum: er bekaeme die Meldung und stuende davor, ohne
// handeln zu koennen. Das ist die «Sehen ohne Duerfen»-Sackgasse, die es hier
// schon einmal gab.
//
// Darum zwei Traeger — und ausdruecklich KEIN dritter fuer die Eskalation:
// wer eskaliert wird, ergibt sich aus der Kombination, nicht aus einem
// weiteren Haekchen.
//
//   darf | sieht routinemaessig | Umfang     | heute
//   -----+----------------------+------------+---------------------------
//   nein | —                    | none       | Corsin
//   ja   | ja                   | all        | Marvin, Jacqueline, Daniel
//   ja   | nein                 | escalation | Sandro
//
// Die vierte Zeile (sehen ohne duerfen) gibt es nicht: Migration 0152
// verbietet sie per CHECK. `leadDistributionScope` faellt trotzdem auf `none`
// zurueck — der Browser-Speicher kann tragen, was die DB nie geschrieben hat.

export type LeadDistributionScope = "none" | "all" | "escalation";

export function leadDistributionScope(
  user: LeadDistributorShape,
): LeadDistributionScope {
  if (!user.mayDistributeLeads) return "none";
  return user.watchesUnassignedLeads ? "all" : "escalation";
}

/**
 * LEAD-11 — dieselbe Frage eine Ebene hoeher: bekommt dieser MENSCH die
 * Verteil-Meldungen ueberhaupt in die Hand?
 *
 * Zwei Schranken, und beide werden gebraucht:
 *   * die LESE-Rolle — Corsin (`pl` ohne Merkmal) und Daniel (`superadmin`,
 *     traegt beide Merkmale, liest den Type aber nicht) stehen draussen;
 *   * das MERKMAL am Menschen (LEAD-8) — nicht die Rolle, sonst saehe Corsin
 *     alles wieder.
 *
 * Bis LEAD-11 stand diese Kombination an zwei Orten: im Meldungs-Projektor
 * (`loadLeadDistributorRecipients`) richtig, und im Team-Alarm-Mail
 * (`sendAdminNewLeadAlert`) als reine Rollen-Liste daneben. Ergebnis: Marvin,
 * der die Leads seit dem 05.08. verteilt, bekam als Einziger die Mail «bitte
 * PL zuweisen» nicht. Zwei Ableitungen derselben Groesse laufen in diesem
 * Haus auseinander — hier ist es jetzt eine.
 *
 * `readRoles` reicht der Aufrufer aus der Notification-Registry herein, damit
 * dieses Modul die Registry nicht importieren muss.
 */
export function leadDistributionRecipientScope(
  user: LeadDistributorShape & {
    readonly role: string;
    readonly active: boolean;
  },
  readRoles: readonly string[],
): LeadDistributionScope {
  if (!user.active) return "none";
  if (!readRoles.includes(user.role)) return "none";
  return leadDistributionScope(user);
}

/**
 * Sieht dieser Empfaenger diesen einen Lead? Das ist die ganze Eskalation in
 * einer Zeile, damit Kachel und Meldungen sie nicht je fuer sich beantworten.
 */
export function leadVisibleInScope(
  scope: LeadDistributionScope,
  deadlineMissed: boolean,
): boolean {
  if (scope === "none") return false;
  if (scope === "all") return true;
  return deadlineMissed;
}

/**
 * Gekaufte Leads — heute genau eine Quelle. Bergler bezahlt Aroundhome pro
 * Lead, und derselbe Lead liegt gleichzeitig bei vier bis fuenf Firmen: wer
 * zuletzt anruft, hat bezahlt und bekommt nichts.
 */
export const PURCHASED_LEAD_ORIGINS: readonly string[] = ["aroundhome"];

/** Aroundhomes Zusage an den Interessenten, in Kalenderstunden. */
export const PURCHASED_LEAD_RESPONSE_HOURS = 24;

export function isPurchasedLeadOrigin(origin: string): boolean {
  return PURCHASED_LEAD_ORIGINS.includes(origin);
}

const HOUR_MS = 60 * 60 * 1000;

/**
 * Der Fristzeitpunkt eines Leads — `null`, wenn er keine Zusage traegt. Ein
 * Lead aus dem Formular oder von Hand hat keine 24-Stunden-Zusage; fuer ihn
 * gibt es keine Frist, die reissen koennte. LEAD-10 laesst diese Faelle
 * bewusst draussen (siehe HANDOVER) — eine geratene Schwelle waere hier eine
 * erfundene Antwort.
 */
export function purchasedLeadDeadlineAt(
  origin: string,
  createdAt: Date,
): Date | null {
  if (!isPurchasedLeadOrigin(origin)) return null;
  return new Date(
    createdAt.getTime() + PURCHASED_LEAD_RESPONSE_HOURS * HOUR_MS,
  );
}

/**
 * LEAD-13 — die Verteil-Frist fuer alles ohne eigene Zusage, in ARBEITSTAGEN.
 * Daniel 2026-08-19: «Marvin und Jacqueline haben einen Arbeitstag, danach
 * kommt Sandro dazu.»
 */
export const UNASSIGNED_LEAD_DISTRIBUTION_WORKDAYS = 1;

/**
 * Welche der zwei Uhren fuer diesen Lead laeuft. Die Kachel und die
 * Nachfass-Mail muessen das sagen koennen — «Frist verpasst» heisst bei einem
 * gekauften Lead etwas anderes (eine Zusage an einen Interessenten, der
 * gleichzeitig bei vier Mitbewerbern liegt) als bei einem aus dem Formular
 * (unsere eigene Hausregel).
 */
export type LeadDeadlineKind = "purchased" | "distribution";

export interface LeadResponseDeadline {
  readonly kind: LeadDeadlineKind;
  readonly at: Date;
}

/**
 * Die EINE Frist eines unverteilten Leads (LEAD-10 fuer gekaufte, LEAD-13 fuer
 * alle uebrigen). Jeder Lead hat seit LEAD-13 genau eine — es gibt keinen Fall
 * mehr, in dem nichts reissen kann.
 *
 * Die Arbeitstag-Haelfte rechnet NICHT selbst: `workdayDeadline` fragt
 * `workdaysBetween`, dieselbe Funktion, aus der die Kachel ihr «wartet X
 * Arbeitstage» zieht. Damit koennen Kachel, Karte und Mail nicht
 * auseinanderlaufen.
 */
export function leadResponseDeadline(
  origin: string,
  createdAt: Date,
): LeadResponseDeadline {
  const purchased = purchasedLeadDeadlineAt(origin, createdAt);
  if (purchased !== null) return { kind: "purchased", at: purchased };
  return {
    kind: "distribution",
    at: workdayDeadline(createdAt, UNASSIGNED_LEAD_DISTRIBUTION_WORKDAYS),
  };
}

/**
 * Die EINE Ableitung von «Frist gerissen» (LEAD-10). Vier Leser: die Kachel
 * (ueber `buildUnassignedLead`), der Meldungs-Projektor, die Nachfass-Mail
 * (LEAD-13) und die Tests. Zwei Ableitungen derselben Groesse sind in diesem
 * Haus schon mehrfach auseinandergelaufen.
 */
export function isLeadResponseDeadlineMissed(
  origin: string,
  createdAt: Date,
  now: Date,
): boolean {
  return now.getTime() > leadResponseDeadline(origin, createdAt).at.getTime();
}

export interface UnassignedLeadInput {
  readonly projectId: string;
  readonly displayName: string;
  /** Kennzeichen aus `deriveLeadOrigin` — `aroundhome`, `website`, `email`, … */
  readonly origin: string;
  readonly createdAt: Date;
  /** LEAD-17: der juengste Beleg, dass ein Mensch mit dem Lead zu tun hatte. */
  readonly contactEvidence?: LeadContactEvidenceView | null;
  /** LEAD-19: der Ansprechpartner (`contact_user_id`), oder null. */
  readonly contactUserId?: string | null;
  readonly contactFullName?: string | null;
  /** LEAD-19: das erste Signal des Kunden («er hat angebissen»), oder null. */
  readonly customerSignal?: LeadCustomerSignal | null;
}

export interface UnassignedLeadItem {
  readonly projectId: string;
  readonly displayName: string;
  readonly origin: string;
  /** Menschenlesbar («Aroundhome», «Website») — eine Ableitung, zwei Sichten. */
  readonly originLabel: string;
  readonly createdAt: string;
  /** Arbeitstage seit dem Eingang (Prinzip 16). */
  readonly waitingWorkdays: number;
  /** Gekaufter Lead => externe 24-Stunden-Zusage. */
  readonly purchased: boolean;
  /** Welche der zwei Uhren hier laeuft (LEAD-13). */
  readonly deadlineKind: LeadDeadlineKind;
  /**
   * ISO-Zeitpunkt der massgeblichen Frist. Seit LEAD-13 traegt JEDER
   * unverteilte Lead eine — vorher stand hier `null`, sobald er nicht gekauft
   * war.
   */
  readonly responseDeadlineAt: string;
  /**
   * Ganze Kalenderstunden bis zur Frist — negativ, wenn sie gerissen ist.
   */
  readonly hoursUntilDeadline: number;
  readonly deadlineMissed: boolean;
  /**
   * LEAD-17 — «Marvin hat geschrieben · 12.08.»: der juengste Beleg, dass
   * ein Mensch mit dem Lead zu tun hatte. `null` = kein Beleg bekannt. Die
   * Kachel zeigt ihn neben der Wartezeit, damit «liegt ohne Projektleiter»
   * nicht mehr wie «unbearbeitet» aussieht.
   */
  readonly contactEvidence: LeadContactEvidenceView | null;
  /**
   * LEAD-19 — der Ansprechpartner: wer dem Kunden gegenuebersteht, solange
   * kein Projektleiter gesetzt ist. `null` = niemand (die Einstellung ist
   * leer oder der Mensch deaktiviert) — der Abschnitt «Ohne Ansprechpartner»
   * der Kachel traegt den Fall.
   */
  readonly contactUserId: string | null;
  readonly contactFullName: string | null;
  /**
   * LEAD-19 — «der Kunde hat geantwortet»: das erste Signal (Portal-Knopf,
   * Frage, Fotos, eingehende Mail, von Hand eingetragener Erstkontakt).
   * Gesetzt heisst: jetzt braucht das Projekt einen Projektleiter — der
   * Abschnitt «Kunde hat geantwortet — Projektleiter setzen».
   */
  readonly customerSignal: LeadCustomerSignal | null;
}

export function buildUnassignedLead(
  input: UnassignedLeadInput,
  now: Date,
): UnassignedLeadItem {
  const purchased = isPurchasedLeadOrigin(input.origin);
  // Dieselbe Funktion, die der Meldungs-Projektor und die Nachfass-Mail
  // fragen — nicht dieselbe Rechnung dreimal (LEAD-10/13).
  const deadline = leadResponseDeadline(input.origin, input.createdAt);
  const deadlineMs = deadline.at.getTime();
  // Aufgerundet Richtung null waere geschmeichelt: «noch 0 h» soll erst
  // stehen, wenn wirklich weniger als eine Stunde bleibt, und «-1 h», sobald
  // die Frist eine volle Stunde her ist.
  const hoursUntilDeadline = Math.trunc((deadlineMs - now.getTime()) / HOUR_MS);
  return {
    projectId: input.projectId,
    displayName: input.displayName,
    origin: input.origin,
    originLabel: leadOriginLabel(input.origin),
    createdAt: input.createdAt.toISOString(),
    waitingWorkdays: workdaysBetween(input.createdAt, now),
    purchased,
    deadlineKind: deadline.kind,
    responseDeadlineAt: deadline.at.toISOString(),
    hoursUntilDeadline,
    deadlineMissed: isLeadResponseDeadlineMissed(
      input.origin,
      input.createdAt,
      now,
    ),
    contactEvidence: input.contactEvidence ?? null,
    contactUserId: input.contactUserId ?? null,
    contactFullName: input.contactFullName ?? null,
    customerSignal: input.customerSignal ?? null,
  };
}

/**
 * LEAD-19 — gehoert dieser Lead in die Kachel «Wer uebernimmt?»? Zwei Faelle,
 * beide brauchen die Hand eines Verteilers: der Kunde hat geantwortet (jetzt
 * einen Projektleiter setzen) — oder niemand steht dem Kunden gegenueber.
 * Ein Lead MIT Ansprechpartner und OHNE Antwort ist dessen Tagesgeschaeft
 * (Richtpreis-Karte, Erstkontakt) und steht nicht in der Kachel.
 */
export type UnassignedLeadSection = "kunde_hat_geantwortet" | "ohne_ansprechpartner";

export function unassignedLeadSection(
  item: Pick<UnassignedLeadItem, "contactUserId" | "customerSignal">,
): UnassignedLeadSection | null {
  if (item.customerSignal !== null) return "kunde_hat_geantwortet";
  if (item.contactUserId === null) return "ohne_ansprechpartner";
  return null;
}

// ---------------------------------------------------------------------------
// LEAD-18 — die Frist in Klartext, mit dem Beleg
// ---------------------------------------------------------------------------
// Gemessen am 08.09.2026 im Block «Wer uebernimmt?»: Kuechler, Item, Gredig
// trugen «Corsin hat geschrieben · 31.08.» UND daneben rot «24-H-FRIST
// VERPASST (VOR 41 TAGEN)». Zwei Wahrheiten auf einer Zeile: die Frist
// zaehlte ab Eingang, obwohl der Beleg sagt, dass Kontakt war. Die 24-Stunden-
// Zusage ist eine Zusage auf KONTAKT — liegt ein Beleg vor, ist sie kein
// Vorwurf mehr; was bleibt, ist «ohne Projektleiter seit N Tagen».
//
// EINE Ableitung fuer Werkbank-Block, Karte und Nachfass-Mail. Die
// Sichtbarkeit (`deadlineMissed`, `leadVisibleInScope`) bleibt davon
// unberuehrt: Sandro sieht die drei weiterhin, weil sie ohne PL liegen — nur
// die Zeile sagt jetzt, was wirklich ist.

export type LeadDeadlineTone = "missed" | "pending" | "contacted";

export interface LeadDeadlineDisplay {
  readonly label: string;
  readonly tone: LeadDeadlineTone;
}

const SHORT_DAY_MONTH = new Intl.DateTimeFormat("de-CH", {
  day: "2-digit",
  month: "2-digit",
  timeZone: "Europe/Zurich",
});

/** Wer hat Kontakt gehabt — Vorname, sonst die Quelle in einem Wort. */
function evidenceActorWord(evidence: LeadContactEvidenceView): string {
  if (evidence.actorFirstName) return evidence.actorFirstName;
  switch (evidence.source) {
    case "customer_reply":
    case "portal_reply":
    case "portal_question":
    case "portal_photos":
      return "Kunde";
    case "bexio_contact":
    case "bexio_invoice":
      return "bexio";
    default:
      return "Team";
  }
}

export function leadDeadlineDisplay(
  item: Pick<
    UnassignedLeadItem,
    | "deadlineKind"
    | "deadlineMissed"
    | "hoursUntilDeadline"
    | "createdAt"
    | "contactEvidence"
  >,
  _now: Date,
): LeadDeadlineDisplay | null {
  if (item.contactEvidence) {
    // LEAD-19: hier stand «· ohne PL seit N Tagen». Ein Lead ohne PL ist seit
    // dem 09.09.2026 kein Mangel mehr — der Ansprechpartner fuehrt ihn, und
    // der Projektleiter kommt, wenn der Kunde anbeisst. Die Zeile sagt nur
    // noch, was belegt ist: wann Kontakt war und mit wem.
    return {
      label: `Kontakt ${SHORT_DAY_MONTH.format(new Date(item.contactEvidence.occurredAt))} (${evidenceActorWord(item.contactEvidence)})`,
      tone: "contacted",
    };
  }
  if (item.deadlineKind === "distribution") {
    return item.deadlineMissed
      ? { label: "Verteil-Frist verpasst", tone: "missed" }
      : null;
  }
  if (item.deadlineMissed) {
    const overdue = Math.abs(item.hoursUntilDeadline);
    if (overdue < 24) {
      return { label: `24-h-Frist verpasst (vor ${String(overdue)} h)`, tone: "missed" };
    }
    const days = Math.floor(overdue / 24);
    return {
      label:
        days === 1
          ? "24-h-Frist verpasst (vor 1 Tag)"
          : `24-h-Frist verpasst (vor ${String(days)} Tagen)`,
      tone: "missed",
    };
  }
  return {
    label: `noch ${String(item.hoursUntilDeadline)} h bis zur 24-h-Frist`,
    tone: "pending",
  };
}

/**
 * «heute eingegangen» / «1 Arbeitstag» / «3 Arbeitstage» — die Wartezeit in
 * Worten. Steht hier und nicht in der Kachel, weil die Nachfass-Mail (LEAD-13)
 * dieselbe Zeile schreibt; zwei Formulierungen derselben Zahl waeren zwei
 * Aussagen ueber denselben Lead.
 */
export function waitingWorkdaysLabel(workdays: number): string {
  if (workdays === 0) return "heute eingegangen";
  if (workdays === 1) return "1 Arbeitstag";
  return `${String(workdays)} Arbeitstage`;
}

/** Prinzip 9: die Liste hat eine Ordnung — der aelteste Lead steht zuoberst. */
export function sortUnassignedLeadsOldestFirst(
  items: readonly UnassignedLeadItem[],
): UnassignedLeadItem[] {
  return [...items].sort(
    (a, b) =>
      new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
  );
}
